import { useEffect, useState } from 'react';
import request from 'lib/request';
import { nameToOption } from 'config/questionOptions';

const ATTRIBUTE_PARAMS = { select: ['id', 'name'], page: 1, pageSize: 0 };

/** Attribute list backing each measure source type. */
const SOURCE_ENDPOINTS = {
  wo: '/settings/wo-custom-attributes',
  company: '/settings/company-attributes',
  custom: '/settings/custom-attributes',
};

// Widgets that mount together (one per question in edit mode) share the
// in-flight request. Every later mount refetches, so attributes added elsewhere
// during the session show up; the last loaded lists are shown meanwhile and
// kept if the refetch fails.
let lastOptions = {};
let pendingRequest = null;

const toNameOptions = (response) =>
  (response?.data ?? [])
    .map((attribute) => attribute?.name)
    .filter(Boolean)
    .map(nameToOption);

const loadOptions = () => {
  pendingRequest =
    pendingRequest ??
    Promise.all(
      Object.entries(SOURCE_ENDPOINTS).map(([type, url]) =>
        request
          .get(url, { params: ATTRIBUTE_PARAMS })
          .then((response) => [type, toNameOptions(response)]),
      ),
    )
      .then((entries) => {
        lastOptions = entries.reduce((options, [type, names]) => {
          options[type] = names;
          return options;
        }, {});
        return lastOptions;
      })
      .catch(() => lastOptions)
      .finally(() => {
        pendingRequest = null;
      });

  return pendingRequest;
};

/**
 * Loads the attribute names available as a reference-date source for a
 * "Date" quantitative question: work object custom fields, company attributes
 * and asset custom attributes. Returns options keyed by source type.
 * Pass `enabled: false` to skip fetching (e.g. for non-Date questions).
 */
export default function useMeasureSourceAttributes(enabled = true) {
  const [options, setOptions] = useState(lastOptions);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    loadOptions().then((result) => {
      if (active) setOptions(result);
    });

    return () => {
      active = false;
    };
  }, [enabled]);

  return options;
}
